import { useCurrentPricesQuery } from '@queries/mStock/mStockQueries.js'
import { useMStockPolling } from '@hooks/useMStockPolling.js'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTimeStore } from '@store/common/useTimeStore.js'
import { hasQueryData } from '@utils/hasQueryData.js'

const CURRENT_PRICE_POLLING_MS = 5000
const EMPTY_PRICE_MAP = {}

export const useCurrentPrice = (assetIds = [], enabled = true) => {
  const { data, fetchStatus, refetch, isError, isLoading } = useCurrentPricesQuery(
    assetIds,
    enabled
  )

  const assetIdCountRef = useRef(assetIds.length)
  assetIdCountRef.current = assetIds.length

  const isFetchingRef = useRef(false)
  isFetchingRef.current = fetchStatus === 'fetching'

  const lastRequestedBoundaryRef = useRef(null)
  const hasMissedBoundaryRef = useRef(false)

  const handleRefetch = useCallback(() => {
    if (!enabled) return

    if (!assetIdCountRef.current) return

    const nowInServerTime = useTimeStore.getState().getBaseTime().getTime()
    const boundary =
      Math.floor(nowInServerTime / CURRENT_PRICE_POLLING_MS) * CURRENT_PRICE_POLLING_MS

    // 중복 요청 방지
    if (lastRequestedBoundaryRef.current === boundary) return

    if (isFetchingRef.current) {
      hasMissedBoundaryRef.current = true
      return
    }

    lastRequestedBoundaryRef.current = boundary

    refetch({ cancelRefetch: false })
  }, [enabled, refetch])

  useEffect(() => {
    if (fetchStatus === 'fetching') return

    if (!hasMissedBoundaryRef.current) return

    hasMissedBoundaryRef.current = false

    handleRefetch()
  }, [fetchStatus, handleRefetch])

  useMStockPolling(handleRefetch, '5s', { enabled })

  const prices = useMemo(() => {
    if (!data?.length) return EMPTY_PRICE_MAP

    return Object.fromEntries(data.map((price) => [price.assetId, price]))
  }, [data])

  const hasData = hasQueryData(prices)

  return useMemo(
    () => ({
      prices,
      isError: isError && !hasData,
      isLoading: isLoading && !hasData,
      refetch
    }),
    [prices, isError, isLoading, hasData, refetch]
  )
}
